import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { aiService } from '../../services/ai.service';
import { AiSettings } from '../../interfaces/ai.interface';
import { getErrorMsg } from '../../utils/error';

const MODEL_SUGGESTIONS = [
  'openai/gpt-4o-mini',
  'openai/gpt-4o',
  'anthropic/claude-3-5-sonnet',
  'anthropic/claude-3-haiku',
  'meta-llama/llama-3.3-8b-instruct:free',
  'google/gemini-flash-1.5',
];

// DeepSeek is called directly (not through OpenRouter), so these are its own
// bare model ids. Both support function calling, which AI chat requires.
const DEEPSEEK_MODEL_SUGGESTIONS = ['deepseek-v4-flash', 'deepseek-v4-pro'];

const EMPTY: AiSettings = {
  model: '',
  fallbackModel: '',
  deepseekModel: '',
  deepseekFallbackModel: '',
};

/** Fields in the order the mobile API actually tries them. */
const FIELDS: Array<{ key: keyof AiSettings; labelKey: string; hintKey: string; list: string }> = [
  {
    key: 'deepseekModel',
    labelKey: 'ai_deepseek_model',
    hintKey: 'ai_deepseek_model_hint',
    list: 'deepseek-model-suggestions',
  },
  {
    key: 'deepseekFallbackModel',
    labelKey: 'ai_deepseek_fallback_model',
    hintKey: 'ai_deepseek_fallback_model_hint',
    list: 'deepseek-model-suggestions',
  },
  { key: 'model', labelKey: 'ai_model', hintKey: 'ai_model_hint', list: 'ai-model-suggestions' },
  {
    key: 'fallbackModel',
    labelKey: 'ai_fallback_model',
    hintKey: 'ai_fallback_model_hint',
    list: 'ai-model-suggestions',
  },
];

const AiSettingsPage = () => {
  const { t } = useTranslation('common');
  const [settings, setSettings] = useState<AiSettings>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await aiService.getSettings();
        setSettings({ ...EMPTY, ...data });
      } catch (e) {
        setError(getErrorMsg(e, t('ai_settings_load_error')));
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [t]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    const trimmed = Object.fromEntries(
      Object.entries(settings).map(([k, v]) => [k, v.trim()]),
    ) as AiSettings;
    try {
      const data = await aiService.updateSettings(trimmed);
      setSettings({ ...trimmed, ...data });
      setSuccess(t('ai_settings_saved'));
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(getErrorMsg(e, t('ai_settings_save_error')));
    } finally {
      setSaving(false);
    }
  };

  // Only the OpenRouter pair is mandatory — the DeepSeek rungs are optional.
  const canSave = settings.model.trim() !== '' && settings.fallbackModel.trim() !== '';

  return (
    <Layout title={t('ai_settings')}>
      <div className="space-y-4 max-w-xl">
        {error && (
          <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">{error}</div>
        )}
        {success && (
          <div className="p-3 rounded-md bg-green-50 text-green-600 border border-green-100">{success}</div>
        )}

        <Card className="p-5 space-y-4">
          {loading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded w-24" />
            </div>
          ) : (
            <>
              <datalist id="ai-model-suggestions">
                {MODEL_SUGGESTIONS.map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
              <datalist id="deepseek-model-suggestions">
                {DEEPSEEK_MODEL_SUGGESTIONS.map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>

              {FIELDS.map(({ key, labelKey, hintKey, list }) => (
                <div key={key}>
                  <label
                    htmlFor={`ai-${key}-input`}
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    {t(labelKey)}
                  </label>
                  <input
                    id={`ai-${key}-input`}
                    type="text"
                    list={list}
                    value={settings[key]}
                    onChange={(e) => setSettings((s) => ({ ...s, [key]: e.target.value }))}
                    disabled={saving}
                    placeholder={t(hintKey)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t(hintKey)}</p>
                </div>
              ))}

              <Button variant="primary" onClick={handleSave} disabled={loading || saving || !canSave}>
                {saving ? t('saving') : t('save')}
              </Button>
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default AiSettingsPage;
